import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import sortBy from 'lodash.sortby';
import { useLocations } from './useLocations';

export const useSyncProfilesFilters = () => {
    const { filterData, filterLoading } = useSelector(state => state.profiles);
    const { mapSelectedLocationsToFormValue, getLocationsOptions } = useLocations();

    const processedData = useMemo(() => {
        if (filterLoading || !filterData || !Array.isArray(filterData)) {
            return {
                locationsValues: [],
                getPlatformsValues: () => [],
                locationsOptions: [],
                getPlatformsOptions: () => [],
            };
        }

        const locationIdsSet = new Set();
        const profileIdsSet = new Set();
        const locationProfilesMap = {};
        const platformsMap = new Map();

        filterData.forEach(item => {
            const profileId = item.integrationType ?? item.listingDirectoryId;

            if (profileId) {
                profileIdsSet.add(profileId);

                if (!platformsMap.has(profileId)) {
                    platformsMap.set(profileId, item.name);
                }
            }

            if (item.locations && Array.isArray(item.locations)) {
                item.locations.forEach(locationId => {
                    locationIdsSet.add(locationId);

                    if (!locationProfilesMap[locationId]) {
                        locationProfilesMap[locationId] = [];
                    }
                    if (profileId && !locationProfilesMap[locationId].includes(profileId)) {
                        locationProfilesMap[locationId].push(profileId);
                    }
                });
            }
        });

        const locationsIds = Array.from(locationIdsSet);

        const locationsValues = mapSelectedLocationsToFormValue(locationsIds);
        const filterCb = l => locationsIds.includes(l.companyId);
        const locationsOptions = getLocationsOptions(filterCb);

        const getPlatformsValues = (locationIds = locationsIds) => {
            const relevantProfileIds = new Set();

            locationIds.forEach(locationId => {
                const profiles = locationProfilesMap[locationId];
                if (profiles) {
                    profiles.forEach(profileId => relevantProfileIds.add(profileId));
                }
            });

            return Array.from(relevantProfileIds);
        };

        const getPlatformsOptions = (locationIds = locationsIds) => {
            const relevantProfileIds = new Set();

            locationIds.forEach(locationId => {
                const profiles = locationProfilesMap[locationId];
                if (profiles) {
                    profiles.forEach(profileId => relevantProfileIds.add(profileId));
                }
            });

            return sortBy(
                Array.from(relevantProfileIds).map(profileId => ({
                    label: platformsMap.get(profileId),
                    value: profileId,
                })),
                item => item.label?.toLowerCase() || '',
            );
        };

        return {
            locationsValues,
            locationsOptions,
            getPlatformsValues,
            getPlatformsOptions,
        };
    }, [filterData]);

    return processedData;
};
